import React, { useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { DateTimePicker } from '@material-ui/pickers';

import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Switch from '@material-ui/core/Switch';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import MenuItem from '@material-ui/core/MenuItem';
import Divider from '@material-ui/core/Divider';

const renderDateInput = props => (
    <TextField
        variant="outlined"
        fullWidth
        onClick={props.onClick}
        id={props.id}
        value={props.value}
        disabled={props.disabled}
        label={props.label}
        helperText={props.helperText}
        {...props.inputProps}
    />
);

const useStyles = makeStyles(theme => ({
    section: {
        marginBottom: theme.spacing(3)
    },
    formContainer: {
        overflowY: 'scroll',
        height: '100%',
        padding: theme.spacing(3)
    },
    marginTop: {
        marginTop: theme.spacing(3)
    },
    image: {
        maxHeight: 300,
        maxWidth: 300,
        height: 'auto',
        width: '100%'
    },
    imageContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        padding: theme.spacing(6)
    },
    height: {
        height: '100%'
    },
    settingsContainer: {
        height: '100%',
        padding: theme.spacing(3)
    },
}));

const forOrdering = [ 'textbox', 'options', 'userselect', 'datetime', 'number' ]

export default function ProposalDetails(props) {

    const {
        election,
        fields,
        handleElection,
        isError,
        getErrorMessage,
        errors,
        groups,
        newElection
    } = props;

    const classes = useStyles();

    const proposalStartDateMessage = useMemo(() => {
        if (isError('proposals_start_at')) return errors['proposals_start_at'];
        return "A blank value will allow proposals to be made at anytime during the election cycle."
    }, [errors]);

    const proposalsPerUserMessage = useMemo(() => {
        if (isError('proposal_per_user')) return errors['proposal_per_user'];
        return "0 will allow no limit on proposals."
    }, [errors]);
    const sponsorsPerProposalMessage = useMemo(() => {
        if (isError('sponsors_per_proposal')) return errors['sponsors_per_proposal'];
        return "Limit to 15. Sponsors must be in the Proposal Group."
    }, [errors]);

    const proposalsRepeatLengthMessage = useMemo(() => {
        if (isError('proposals_repeat_length')) return errors['proposals_repeat_length'];
        return "0 will keep a proposal indefinitely."
    }, [errors]);

    const initialEndDate = useMemo(() => {

        const ied = election.starts_at.plus({[election.repeat_period]: election.repeat_amount})
        const nied = (election.proposals_start_at) ? election.proposals_start_at.plus({weeks: 2}).startOf('hour') : election.starts_at.plus({weeks: 2}).startOf('hour');

        return (ied > nied) ? nied : ied;

    }, [election.starts_at, election.proposals_start_at]);

    return (
        <Grid container direction="row" className={newElection ? classes.height : null}>
            {newElection &&
                <Grid item xs className={classes.height}>
                    <div className={classes.imageContainer}>
                        <img className={classes.image} src='/images/step-proposal.png' alt="proposal-graphic" />
                    </div>
                </Grid>
            }
            <Grid item xs={newElection ? 7 : 12} className={newElection ? classes.formContainer : classes.settingsContainer}>
                {newElection &&
                    <Typography variant='h6' gutterBottom>
                        Proposals
                    </Typography>
                }
                <FormControl>
                    <FormControlLabel
                        margin="normal"
                        control={
                            <Switch
                                checked={election.proposals_active}
                                onChange={handleElection('proposals_active')}
                                color="primary"
                                value="proposals"
                            />
                        }
                        label="Allow users to create proposals?"
                        labelPlacement="end"
                    />
                    <FormHelperText>
                        When disabled, administrators will still be able to create proposals
                    </FormHelperText>
                </FormControl>
                <div className={classes.section}>
                    <Typography variant='subtitle2' gutterBottom>
                        Select the group of users that can create proposals.
                    </Typography>
                    <TextField
                        id="input-proposal-group-id"
                        select
                        value={election.proposal_group_id}
                        onChange={handleElection('proposal_group_id')}
                        variant="outlined"
                        fullWidth
                        error={isError('proposal_group_id')}
                        helperText={getErrorMessage('proposal_group_id')}
                        disabled={! election.proposals_active}
                        >
                        <MenuItem value={0}>
                            All Users
                        </MenuItem>
                        {groups.map(group => (
                            <MenuItem key={group.id} value={group.id}>
                                {group.title}
                            </MenuItem>
                        ))}
                    </TextField>
                </div>
                <div className={classes.section}>
                    <Typography variant='subtitle2' gutterBottom>
                        What date and time should users be allowed to create proposals?
                    </Typography>
                    <DateTimePicker
                        clearable
                        label="Proposals Start Date"
                        inputVariant="outlined"
                        value={election.proposals_start_at}
                        onChange={handleElection('proposals_start_at')}
                        variant="outlined"
                        TextFieldComponent={renderDateInput}
                        minutesStep={15}
                        disabled={! election.proposals_active}
                        helperText={proposalStartDateMessage}
                        initialFocusedDate={election.starts_at}
                    />
                </div>
                <div className={classes.section}>
                    <Typography variant='subtitle2' gutterBottom>
                        What date and time should proposals close?
                    </Typography>
                    <DateTimePicker
                        clearable
                        label="Proposals End Date"
                        inputVariant="outlined"
                        value={election.proposals_end_at}
                        onChange={handleElection('proposals_end_at')}
                        variant="outlined"
                        TextFieldComponent={renderDateInput}
                        minutesStep={15}
                        disabled={! election.proposals_active}
                        initialFocusedDate={initialEndDate}
                    />
                </div>
                <div className={classes.section}>
                    <Typography variant='subtitle2' gutterBottom>
                        How many proposals can a user make?
                    </Typography>
                    <TextField
                        id="input-proposals-per-user"
                        value={election.proposals_per_user}
                        onChange={handleElection('proposals_per_user')}
                        fullWidth
                        type="number"
                        error={isError('proposals_per_user')}
                        helperText={proposalsPerUserMessage}
                        variant="outlined"
                        disabled={! election.proposals_active}
                    />
                </div>
                <div className={classes.section}>
                    <Typography variant='subtitle2' gutterBottom>
                        How many sponsors can a user choose for the proposal other than themselves?
                    </Typography>
                    <TextField
                        id="input-sponsors-per-proposal"
                        value={election.sponsors_per_proposal}
                        onChange={handleElection('sponsors_per_proposal')}
                        fullWidth
                        type="number"
                        max="15"
                        error={isError('sponsors_per_proposal')}
                        helperText={sponsorsPerProposalMessage}
                        variant="outlined"
                        disabled={! election.proposals_active}
                    />
                </div>
                <div className={classes.section}>
                    <FormControlLabel
                        margin="normal"
                        control={
                            <Switch
                                checked={election.proposals_starting_notifications}
                                onChange={handleElection('proposals_starting_notifications')}
                                color="primary"
                                value="proposals_starting_notifications"
                            />
                        }
                        label="Should Proposal Group Members be sent a notification once they can make proposals?"
                        labelPlacement="end"
                        disabled={(!election.proposals_active)}
                    />
                    <FormControlLabel
                        margin="normal"
                        control={
                            <Switch
                                checked={election.proposals_ending_notifications}
                                onChange={handleElection('proposals_ending_notifications')}
                                color="primary"
                                value="proposals_ending_notifications"
                            />
                        }
                        label="Should Proposal Group Members who did not make a proposal receive a warning before proposals close?"
                        labelPlacement="end"
                        disabled={(!election.proposals_active)}
                    />
                </div>
                <Divider className={classes.section}/>
                <div className={classes.section}>
                    <FormControlLabel
                        margin="normal"
                        control={
                            <Switch
                                checked={election.proposals_repeated}
                                onChange={handleElection('proposals_repeated')}
                                color="primary"
                                value="proposals_repeated"
                            />
                        }
                        label="Should proposals to be passed to next election cycle?"
                        labelPlacement="end"
                        disabled={(!election.repeated)}
                    />
                </div>
                <div className={classes.section}>
                    <Typography variant='subtitle2' gutterBottom>
                        How many election cycles should a proposal be kept for?
                    </Typography>
                    <TextField
                        id="input-proposals-repeat-length"
                        value={election.proposals_repeat_length}
                        onChange={handleElection('proposals_repeat_length')}
                        fullWidth
                        type="number"
                        error={isError('proposals_repeat_length')}
                        helperText={proposalsRepeatLengthMessage}
                        variant="outlined"
                        disabled={!election.proposals_repeated}
                    />
                </div>
                {! newElection &&
                    <>
                        <Divider className={classes.section}/>
                        <div className={classes.section}>
                            <Typography variant='subtitle2' gutterBottom>
                                Select the field to order proposals by.
                            </Typography>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        id="select-order-field-id"
                                        select
                                        label="Order Proposals By"
                                        value={election.order_field_id}
                                        onChange={handleElection('order_field_id')}
                                        variant="outlined"
                                        fullWidth
                                        error={isError('order_field_id')}
                                        helperText='Must have created a form to populate field options.'
                                        >
                                        <MenuItem key={0} value={0}>
                                            By Entry
                                        </MenuItem>
                                        {Object.keys(fields).map((i) => forOrdering.includes(fields[i].options.type) ? (
                                            <MenuItem key={fields[i].id} value={fields[i].id}>
                                                {fields[i].options.label}
                                            </MenuItem>
                                        ) : null )}
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormControlLabel
                                        margin="normal"
                                        control={
                                            <Switch
                                                checked={election.order_field_dir}
                                                onChange={handleElection('order_field_dir')}
                                                color="primary"
                                                value="order_field_dir"
                                            />
                                        }
                                        label="Reverse Order of Proposals?"
                                        labelPlacement="end"
                                        disabled={(election.order_field_id === 0)}
                                    />
                                </Grid>
                            </Grid>
                        </div>
                    </>
                }
            </Grid>
        </Grid>
    )
}
